import React from "react";
import {
  FaMapMarkerAlt,
  FaTruck,
  FaMotorcycle,
  FaCalendarAlt,
  FaHeadset,
  FaStickyNote,
} from "react-icons/fa";
import useStore from "../../../store/useStore";

const OrderConfirmedInfoGrid = ({ order, onDownloadInvoice, onChatSupport }) => {
  const { cart_meta } = useStore();
  const deliveryOptions = cart_meta?.delivery_options || [];

  const deliveryName =
    order?.delivery_name ||
    order?.delivery_address?.name ||
    order?.address?.name ||
    "";
  const deliveryAddress =
    typeof order?.delivery_address === "string"
      ? order.delivery_address
      : order?.delivery_address?.address || order?.address?.address || "";
  const deliveryCity =
    order?.delivery_city ||
    order?.delivery_address?.city ||
    order?.address?.city ||
    "";
  const deliveryState =
    order?.delivery_state ||
    order?.delivery_address?.state ||
    order?.address?.state ||
    "";
  const deliveryPincode =
    order?.delivery_pincode ||
    order?.delivery_address?.pincode ||
    order?.address?.pincode ||
    "";
  const deliveryMobile =
    order?.delivery_mobile ||
    order?.delivery_address?.mobile ||
    order?.address?.mobile ||
    "";

  // Dynamic delivery option details from API
  const rawDeliveryType = String(order?.delivery_type || "STANDARD").toUpperCase();

  const matchedOpt = deliveryOptions.find(
    (o) => String(o.type).toUpperCase() === rawDeliveryType
  );

  let deliveryFeeNum = parseFloat(order?.delivery_fee ?? 0);
  if (matchedOpt && matchedOpt.fee != null) {
    deliveryFeeNum = Number(matchedOpt.fee);
  }

  const slotText =
    order?.delivery_slot_name ||
    order?.slot_name ||
    (order?.notes?.startsWith("Time slot:")
      ? order.notes.replace("Time slot:", "").trim()
      : "");

  // Clean notes: strip "Time slot: ..." prefix (shown separately on Delivery card)
  const cleanNotes = order?.clean_notes !== undefined
    ? order.clean_notes
    : (order?.notes || "").replace(/Time slot:\s*[^|]+(\s*\|\s*)?/i, "").trim();

  const getDeliveryConfig = (type) => {
    switch (type) {
      case "EXPRESS":
        return { Icon: FaMotorcycle, colorClass: "text-amber-600" };
      case "SCHEDULED":
        return { Icon: FaCalendarAlt, colorClass: "text-[#046a38]" };
      default:
        return { Icon: FaTruck, colorClass: "text-blue-600" };
    }
  };

  const deliveryTitle = matchedOpt?.title || matchedOpt?.name || rawDeliveryType;
  const { Icon: DeliveryIcon, colorClass } = getDeliveryConfig(rawDeliveryType);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {/* 1. Delivery Address Card */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 mb-2">
            <FaMapMarkerAlt className="h-4 w-4 text-[#046a38]" />
            <span>Delivery Address</span>
          </div>
          <p className="text-xs text-gray-700 leading-relaxed font-medium">
            {deliveryName ? (
              <span className="block font-bold text-gray-900">{deliveryName}</span>
            ) : null}
            {deliveryAddress}
            {deliveryCity ? `, ${deliveryCity}` : ""}
            {deliveryState ? `, ${deliveryState}` : ""}
            {deliveryPincode ? ` - ${deliveryPincode}` : ""}
          </p>
          {deliveryMobile && (
            <p className="text-[11px] text-gray-500 mt-1 font-semibold">
              Ph: {deliveryMobile}
            </p>
          )}
        </div>
      </div>

      {/* 2. Dynamic Delivery Option Card */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-900 mb-2">
            <DeliveryIcon className={`h-4 w-4 ${colorClass}`} />
            <span>Delivery Option</span>
          </div>
          <p className={`text-xs sm:text-sm font-bold ${colorClass}`}>{deliveryTitle}</p>
          {slotText && (
            <p className="text-[11px] font-semibold text-gray-700 mt-1 flex items-center gap-1">
              <span>🗓️</span>
              <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200 font-bold">
                {slotText}
              </span>
            </p>
          )}
          {deliveryFeeNum > 0 && (
            <p className="text-[11px] font-semibold mt-2 text-gray-700">
              Delivery Fee: <strong className="text-gray-900">₹{deliveryFeeNum.toFixed(2)}</strong>
            </p>
          )}
        </div>
      </div>

      {/* 3. Need Help Card */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-gray-900 mb-2">
            <FaHeadset className="h-4 w-4 text-[#046a38]" />
            <span>Need Help?</span>
          </div>
          <p className="text-xs text-gray-500">
            Have questions about your order or delivery?
          </p>
        </div>
        <button
          onClick={onChatSupport}
          className="mt-3 w-full rounded-lg border border-[#046a38] py-1.5 text-xs font-bold text-[#046a38] hover:bg-emerald-50 transition"
        >
          Contact with Us
        </button>
      </div>

      {/* 4. Delivery Notes Card — only shown when notes exist */}
      {cleanNotes && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold text-gray-900 mb-2">
              <FaStickyNote className="h-4 w-4 text-amber-500" />
              <span>Delivery Notes</span>
            </div>
            <p className="text-xs text-amber-900 leading-relaxed font-medium break-words whitespace-pre-line">
              {cleanNotes}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderConfirmedInfoGrid;
